import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { getProjectBySlug } from "../data/projects";

export default function ProjectPage() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const thumbRefs = useRef([]);

  const images = useMemo(() => project?.images || [], [project]);
  const hasMultiple = images.length > 1;

  // Preload images
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  // Auto-advance slideshow — slow, elegant
  useEffect(() => {
    if (isPaused || !hasMultiple) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPaused, hasMultiple, images.length]);

  // Keep active thumbnail in view
  useEffect(() => {
    const el = thumbRefs.current[currentSlide];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [currentSlide]);

  const pauseBriefly = useCallback(() => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  }, []);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
    pauseBriefly();
  }, [pauseBriefly, images.length]);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
    pauseBriefly();
  }, [pauseBriefly, images.length]);

  const goTo = useCallback((idx) => {
    setCurrentSlide(idx);
    pauseBriefly();
  }, [pauseBriefly]);

  // Bespoke page / missing project / coming soon → send back to portfolio
  if (!project || project.dedicatedPage || project.comingSoon) {
    return <Navigate to="/portfolio" replace />;
  }

  const categoryLabel = project.category === "vip" ? "VIP Suite" : project.category;
  const imgClass = `absolute inset-0 w-full h-full object-contain transition-opacity duration-[1200ms] ease-in-out ${project.enhanceColors ? "enhance-colors" : ""}`;

  return (
    <div data-testid="project-page" className="bg-cream">

      {/* Editorial Header */}
      <section className="pt-28 md:pt-36 pb-8 md:pb-12" data-testid="project-header">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-black/40 hover:text-warmBrown transition-colors duration-300 font-body text-xs uppercase tracking-[0.2em] mb-10"
            data-testid="project-back-link"
          >
            <ArrowLeft size={14} />
            Back to Projects
          </Link>

          <p className="text-warmBrown uppercase tracking-[0.3em] text-xs font-body mb-4">
            {categoryLabel}
          </p>

          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-black leading-[1.05] mb-4" data-testid="project-title">
            {project.title}
          </h1>

          <p className="text-black/40 uppercase tracking-[0.2em] text-xs font-body mb-8">
            {project.location}
          </p>

          <p className="text-black/60 font-body text-sm md:text-base leading-[1.85] max-w-2xl mx-auto">
            {project.description}
          </p>
        </div>
      </section>

      {/* Full-width Centered Slideshow */}
      <section className="pb-16 md:pb-24" data-testid="project-slideshow">
        <div
          className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Main Viewer */}
          <div className="relative" data-testid="project-main-viewer">
            <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden">
              {images.map((src, idx) => (
                <img
                  key={src}
                  src={src}
                  alt={`${project.title} ${idx + 1}`}
                  className={imgClass}
                  style={{ opacity: idx === currentSlide ? 1 : 0 }}
                />
              ))}
            </div>

            {hasMultiple && (
              <>
                {/* Side Arrows */}
                <button
                  onClick={goPrev}
                  className="absolute left-2 md:-left-4 top-1/2 -translate-y-1/2 text-black/50 hover:text-warmBrown bg-cream/70 hover:bg-cream p-2 md:p-3 transition-all duration-300 backdrop-blur-sm"
                  data-testid="project-prev"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={26} strokeWidth={1.25} />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-2 md:-right-4 top-1/2 -translate-y-1/2 text-black/50 hover:text-warmBrown bg-cream/70 hover:bg-cream p-2 md:p-3 transition-all duration-300 backdrop-blur-sm"
                  data-testid="project-next"
                  aria-label="Next image"
                >
                  <ChevronRight size={26} strokeWidth={1.25} />
                </button>

                {/* Counter */}
                <div className="absolute bottom-3 right-3 bg-black/30 backdrop-blur-sm text-white/90 text-xs font-body tracking-[0.15em] px-3 py-1">
                  {String(currentSlide + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail Strip */}
          {hasMultiple && (
            <div
              className="mt-4 md:mt-6 flex gap-2 md:gap-3 overflow-x-auto pb-2"
              data-testid="project-thumb-strip"
            >
              {images.map((src, idx) => (
                <button
                  key={src}
                  ref={(el) => (thumbRefs.current[idx] = el)}
                  onClick={() => goTo(idx)}
                  className={`relative flex-shrink-0 w-20 h-16 md:w-28 md:h-20 overflow-hidden transition-all duration-300 ${
                    idx === currentSlide
                      ? "ring-2 ring-warmBrown opacity-100"
                      : "opacity-50 hover:opacity-90"
                  }`}
                  data-testid={`project-thumb-${idx}`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img src={src} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}

          {/* Services */}
          {project.services?.length > 0 && (
            <div className="max-w-2xl mx-auto text-center mt-14 md:mt-20">
              <p className="text-warmBrown uppercase tracking-[0.3em] text-xs font-body mb-6">
                Services Provided
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {project.services.map((service) => (
                  <span
                    key={service}
                    className="text-xs text-warmBrown bg-warmBrown/10 px-3 py-1 font-body"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-warmBrown text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            Ready to Create Your Space?
          </h2>
          <p className="text-white/70 font-body text-sm md:text-base mb-8 max-w-lg mx-auto">
            Every great space starts with a conversation. Tell us about your vision.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-white text-black hover:bg-champagne transition-all duration-500 uppercase tracking-[0.2em] text-xs px-8 py-4 font-semibold"
            data-testid="project-cta"
          >
            Start Your Project
          </Link>
        </div>
      </section>
    </div>
  );
}
